import { ReactElement, useCallback } from 'react';
import { AxiosResultDefaultError } from '../../../../../api/request';
import { AuthenticationError } from '../../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../components/info/DisplayError';
import Loading from '../../../../../components/info/Loading';
import { useStudentCurrentAccountLoader } from '../../components/StudentCurrentAccountLoader';
import { getDebts } from '../api';
import { DebtFiltersState, DebtResponse, EMPTY_DEBT_FILTERS } from '../types';

interface BaseLoaderProps {
  children: ReactElement;
}

// HU #15319 — the loader's query is the DebtFilters state. `DebtFilters`
// reads/writes it via `useDebtLoader().query` / `updateQuery(...)`, which
// triggers a refetch against `GET .../debts?year=...&period=...&...`.
const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  DebtFiltersState,
  DebtResponse,
  AuthenticationError | AxiosResultDefaultError
>();

export function useDebtLoader() {
  return useFetch();
}

export const debtLoaderConsumer = FetchConsumer;

export default function DebtLoader({ children }: BaseLoaderProps) {
  const { data } = useStudentCurrentAccountLoader();
  const studentId = data!.studentId;
  const request = useCallback(
    async (filters: DebtFiltersState) => {
      return getDebts(studentId, filters);
    },
    [studentId],
  );
  return (
    <FetchProvider
      request={request}
      defaultQuery={EMPTY_DEBT_FILTERS}
      fetchImmediately={true}
    >
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading && !data) {
            return <Loading insideCard />;
          } else if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
              />
            );
          } else if (data) {
            return children;
          } else {
            return null;
          }
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
